// Admin session: JWT from POST /auth/login, kept in memory + localStorage (keyed by API base URL).
// Shared across tabs via the "storage" event. No provider needed.
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

const PREFIX    = "florium.session:";
const cache     = new Map();
const listeners = new Set();

function decodeExp(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64)).exp || null;
  } catch {
    return null;
  }
}

function load(api) {
  let s = null;
  try { s = JSON.parse(localStorage.getItem(PREFIX + api) || "null"); } catch {}
  if (!s || !s.token || (s.exp && s.exp * 1000 <= Date.now())) return null;
  return s;
}

function read(api) {
  if (!api) return null;
  if (!cache.has(api)) cache.set(api, load(api));
  return cache.get(api);
}

function write(api, s) {
  cache.set(api, s);
  try {
    if (s) localStorage.setItem(PREFIX + api, JSON.stringify(s));
    else   localStorage.removeItem(PREFIX + api);
  } catch {}
  listeners.forEach(l => l());
}

function onStorage(e) {
  if (e.key && e.key.startsWith(PREFIX)) {
    cache.delete(e.key.slice(PREFIX.length));
    listeners.forEach(l => l());
  }
}

function subscribe(cb) {
  listeners.add(cb);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

export async function login(api, email, password) {
  const res  = await fetch(`${api}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim(), password: password.trim() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.token) throw new Error(data.error || "Login failed");
  write(api, { token: data.token, user: data.user, exp: decodeExp(data.token) });
  return data.user;
}

export function logout(api) {
  write(api, null);
}

export function useSession(api) {
  return useSyncExternalStore(subscribe, () => read(api), () => null);
}

export function useLoginForm(api, onSuccess) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [busy, setBusy]         = useState(false);

  const submit = useCallback(async () => {
    if (busy) return;
    if (!email.trim() || !password) { setError("Enter your email and password."); return; }
    setBusy(true);
    setError("");
    try {
      const user = await login(api, email, password);
      setPassword("");
      onSuccess?.(user);
    } catch (e) {
      setError(e.message || "Login failed");
      setPassword("");
    } finally {
      setBusy(false);
    }
  }, [api, email, password, busy, onSuccess]);

  return { email, setEmail, password, setPassword, error, setError, busy, submit };
}

// Teacher scope from /auth/me: array of classIds, null = unrestricted (owner), undefined = loading.
const scopeCache = new Map();
export function useScope(api) {
  const session = useSession(api);
  const token   = session?.token;
  const [scope, setScope] = useState(() => (token && scopeCache.has(token) ? scopeCache.get(token) : undefined));
  useEffect(() => {
    if (!token) { setScope(undefined); return; }
    if (scopeCache.has(token)) { setScope(scopeCache.get(token)); return; }
    let cancelled = false;
    fetch(`${api}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const ids = d?.scope?.classIds ?? null;
        scopeCache.set(token, ids);
        if (!cancelled) setScope(ids);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, token]);
  return scope;
}

export async function changePassword(api, currentPassword, newPassword) {
  const s = read(api);
  if (!s?.token) throw new Error("Not logged in");
  const res = await fetch(`${api}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.token}` },
    body: JSON.stringify({ currentPassword: currentPassword.trim(), newPassword: newPassword.trim() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.token) throw new Error(data.error || "Could not change password");
  write(api, { token: data.token, user: data.user, exp: decodeExp(data.token) });
}
